import { DateTime } from "luxon";
import { fenceValueWithVat, otherValueWithVat } from "@/utils";
import { pdf_contract, CONTRACT_FORM_CODE } from "@/documents/contract/pdf_contract.js";
import { moduleSizeText, moduleHeightText, moduleNameKey, isPost } from "@/utils/fenceModules";

export default {
  create(contract, executantDetails, firmaMontajContract) {
    return {
      contract,
      executantDetails,
      firmaMontajContract,

      get language() {
        return 'ro-RO'
      },

      get isMontajSeparat() {
        return this.contract.detalii.montaj_separat
      },

      get sumaContract() {
        return fenceValueWithVat(this.contract) + otherValueWithVat(this.contract)
      },

      get sumaAvans() {
        return (this.sumaContract * this.contract.c_plata) / 100
      },

      get sumaAvans2() {
        return this.contract.c_plata2 ? (this.sumaContract * this.contract.c_plata2) / 100 : null
      },

      get sumaRest() {
        const avans2 = this.sumaAvans2 || 0
        return this.sumaContract - this.sumaAvans - avans2
      },

      get executantFirmaText() {
        const calitate = this.isMontajSeparat ? 'EXECUTANT' : 'EXECUTANT si MONTATOR'
        return `1. S.C. ${this.executantDetails.name}, cu sediul in ${this.executantDetails.address}, inregistrata la Registrul Comertului sub numarul ${this.executantDetails.regCom}, cod fiscal ${this.executantDetails.cui}, cont bancar: ${this.executantDetails.bank_account}, ${this.executantDetails.bank_name}, telefon: ${this.executantDetails.phone}, reprezentata legal prin ${this.executantDetails.legal_representative} in calitate de ${calitate}`
      },

      get montajFirmaText() {
        if (!this.firmaMontajContract || !this.firmaMontajContract.denumire_firma) return ''
        return `2. S.C. ${this.firmaMontajContract.denumire_firma.toUpperCase()} S.R.L., cu sediul in ${this.firmaMontajContract.adresa} inregistrata in Registrul Comertului sub numarul ${this.firmaMontajContract.regcom}, cod fiscal ${this.firmaMontajContract.cui} (${Number(this.firmaMontajContract.vat) ? "platitor" : "neplatitor"} de TVA), cont bancar: ${this.firmaMontajContract.bank_account}, ${this.firmaMontajContract.bank}, reprezentata legal prin ${this.firmaMontajContract.person}, in calitate de MONTATOR`
      },


      get actClient() {
        if (this.contract.detalii.alt_document) {
          return this.contract.detalii.alt_document
        }
        return `C.I. serie ${this.contract.detalii.serie_ci} nr. ${this.contract.detalii.numar_ci}`
      },

      get adresaClient() {
        if (this.contract.persoana) {
          return `cu sediul in ${this.contract.adresa}, inregistrata la Registrul Comertului, sub numarul ${this.contract.reg_com}, cod fiscal ${this.contract.cui}, reprezentata legal prin ${this.contract.persoana},`
        } else {
          return `cu domiciliul in ${this.contract.detalii.adresa}, identificat cu ${this.actClient}, ${this.contract.detalii.cnp ? "CNP " + this.contract.detalii.cnp + ", " : ""}`
        }
      },

      get contactClient() {
        const tel = String(this.contract.tel || "").trim()
        const email = String(this.contract.email || "").trim()
        return [
          tel ? `telefon: ${tel}` : null,
          email ? `email: ${email}` : null
        ].filter(Boolean)
      },

      get moduleGard() {
        return this.contract.gard_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              // a post has no width, so it is quoted by height alone
              text: isPost(modul.tip)
                ? `${this.denumireModulGard(modul)} - ${modul.buc} buc, H ${moduleHeightText(modul)} mm;`
                : `${this.denumireModulGard(modul)} - ${modul.buc} buc cu dimensiunile L x h de ${moduleSizeText(modul)} mm;`
            }]
          }]
        }))
      },

      get moduleCustom() {
        if (!this.contract.alte_obj) return []
        return this.contract.alte_obj.map(modul => ({
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              text: `${modul.denumire} - ${modul.cantitate} ${modul.um}`
            }]
          }]
        }))
      },

      get plata1() {
        const upfrontPayments = [{
          type: "listItem",
          content: [{
            type: "paragraph",
            attrs: { textAlign: "left" },
            content: [{
              type: "text",
              text: `${(this.sumaAvans / 1.21).toFixed(2)} euro + TVA, respectiv ${this.sumaAvans.toFixed(2)} euro (TVA inclus) vor fi platiti la semnarea contractului catre EXECUTANT (${this.executantDetails.name}), in contul bancar indicat pe factura,`
            }]
          }]
        }]

        if (this.contract.c_plata2) {
          upfrontPayments.push({
            type: "listItem",
            content: [{
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `${(this.sumaAvans2 / 1.21).toFixed(2)} euro + TVA, respectiv ${this.sumaAvans2.toFixed(2)} euro (TVA inclus) vor fi platiti la efectuarea masuratorilor catre EXECUTANT (${this.executantDetails.name}), in contul bancar indicat pe factura,`
              }]
            }]
          })
        }

        return upfrontPayments
      },

      get plata2() {
        if (this.contract.detalii.montaj_separat) {
          return `${this.sumaRest.toFixed(2)} euro urmand a fi achitati la receptia finala a lucrarii, catre MONTATOR sau catre EXECUTANT, in functie de caz, in contul bancar indicat pe factura.`
        } else {
          return `${(this.sumaRest / 1.21).toFixed(2)} euro + TVA, respectiv ${this.sumaRest.toFixed(2)} euro (TVA inclus) urmand a fi achitati la receptia finala a lucrarii, catre EXECUTANT (${this.executantDetails.name}), in contul bancar indicat pe factura.`
        }
      },

      get p1TermenExecutie() {
        if (this.contract.detalii.finisaj_finalizat) {
          return `Conform informatiei furnizare de catre beneficiar la data intocmirii acestui contract, partea de lucrare ce cade in sarcina sa, si anume, partea de zidarie, beton, stalpi de metal, etc., pe care se vor prinde reperele din aluminiu ce vor fi confectionate de executant, este complet finisata si se pot face masuratorile finale. In acest caz termenul de executie al acestor repere este de ${this.contract.t_executie} zile de la data creditarii contului beneficiarului cu suma stabilita la art VI.`
        } else {
          return `Avand in vedere ca, la data intocmirii acestui contract, beneficiarul nu are finalizata partea de lucrare ce cade in sarcina sa, si anume, partea de zidarie, beton, stalpi de metal, etc., pe care se vor prinde reperele din aluminiu ce vor fi confectionate de executant, termenul de executie a acestor repere este de ${this.contract.t_executie} zile de la data in care executantul va putea sa faca masuratorile finale.`
        }
      },

      get p2TermenExecutie() {
        if (this.contract.detalii.finisaj_finalizat) {
          return `Daca in momentul deplasarii pentru efectuarea masuratorilor, executantul constata ca beneficiarul nu are finalizata partea de lucrare ce cade in sarcina sa, se va intocmi un act aditional in care se va metiona acest lucru iar termenul de executie va fi de ${this.contract.t_executie} zile de la data in care se vor putea face masuratorile finale.`
        } else {
          return `In acest sens se va intocmi un act aditional la contract in care se va mentiona faptul ca beneficiarul are finalizata partea de lucrare ce cade in sarcina sa si executantul poate lua masuratorile finale.`
        }
      },

      get exemplareContract() {
        return this.isMontajSeparat ? "3 (trei)" : "2 (doua)"
      },

      // Helper methods
      // What a module is called in this contract, keyed by type and variant. Adding a type is a
      // key here and a line in moduleNameKey - never another branch below.
      moduleNames: {
        panel: "Panou",
        pedestrianGate: "Poartă pietonală",
        pedestrianGateSidePanel: "Poartă pietonală cu panou lateral",
        swingGateSingleLeaf: "Poartă auto batantă 1 canat",
        swingGateDoubleLeaf: "Poartă auto batantă 2 canate",
        swingGateBiFoldSingle: "Poartă auto batantă 1 canat bi-fold",
        swingGateBiFoldDouble: "Poartă auto batantă 2 canate bi-fold",
        slidingGateSingleLeaf: "Poartă auto culisantă 1 canat",
        slidingGateDoubleLeaf: "Poartă auto culisantă 2 canate",
        slidingGateTelescopic: "Poartă auto culisantă telescopică",
        slidingGateArticulated: "Poartă auto culisantă articulată",
        selfSupportingGate: "Poartă auto autoportantă",
        posts: "Stâlpi",
        post80: "Stâlp 80 × 80 mm",
        post100: "Stâlp 100 × 100 mm",
        post200: "Stâlp 200 × 100 mm",
        framedPanel: "Panou cu ramă"
      },

      denumireModulGard(modul) {
        const key = moduleNameKey(modul.tip, modul.tip_poarta_mare, modul.tip_stalp)
        // a type with no key only ever comes from an offer saved with something since retired
        let name = (key && this.moduleNames[key]) || String(modul.tip || "").replace(/_/g, " ")
        if (modul.pp_included) name += " cu poarta pietonala integrata"
        return name
      },

      formatParagraph(textContent, alignment, textType) {
        return {
          type: "paragraph",
          attrs: { textAlign: alignment },
          content: [{
            type: "text",
            text: textContent,
            ...(textType && { marks: [{ type: textType }] })
          }]
        }
      },

      // Main document structure builder
      buildDocumentStructure() {
        return {
          type: "doc",
          content: [
            // Header
            {
              type: "heading",
              attrs: { textAlign: "center", level: 1 },
              content: [{ type: "text", text: "CONTRACT DE EXECUTIE LUCRARI" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "center" },
              content: [{
                type: "text",
                text: `Nr: ${this.contract.id_contract} / ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")}`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // I. PARTILE CONTRACTANTE
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "I. PARTILE CONTRACTANTE" }]
            },
            this.formatParagraph(this.executantFirmaText, "left"),
            this.isMontajSeparat ? this.formatParagraph("SI", "left") : { type: "blockquote" },
            this.isMontajSeparat ? this.formatParagraph(this.montajFirmaText, "left") : { type: "blockquote" },
            this.formatParagraph("SI", "left"),
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `${this.isMontajSeparat ? "3" : "2"}. ${this.contract.nume.toUpperCase()}, ${[this.adresaClient.trim().replace(/,$/, ""), ...this.contactClient].join(", ")} in calitate de BENEFICIAR`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // II. OBIECTUL CONTRACTULUI
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "II. OBIECTUL CONTRACTULUI" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Obiectul contractului il reprezinta executarea unei lucrari denumita generic ${this.contract.detalii.denumire.toUpperCase()}, conform ofertei nr. ${this.contract.oferta_id} / ${DateTime.fromISO(String(this.contract.oferta_data)).toFormat("dd.MM.yyyy")}`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `EXECUTANTUL se obliga sa execute,${this.contract.detalii.montaj_separat ? " MONTATORUL se obliga" : ""} sa monteze, sa testeze si sa finalizeze lucrarea, iar BENEFICIARUL se obliga sa receptioneze si sa achite contravaloarea lucrarii ce face obiectul prezentului contract.`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Locatia executarii lucrarii ${this.contract.detalii.denumire.toUpperCase()}: ${this.contract.detalii.alta_locatie ? this.contract.detalii.locatie : this.contract.detalii.adresa}.`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // III. DESCRIEREA LUCRARII
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "III. DESCRIEREA LUCRARII" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Elementele componente ale lucrarii denumite ${this.contract.detalii.denumire.toUpperCase()} sunt:`
              }]
            },
            { type: "bulletList", content: this.moduleGard },
            { type: "bulletList", content: this.moduleCustom },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Toate elementele vor fi vopsite in camp electrostatic culoarea ${this.contract.color}, conform specificatiei beneficiarului.`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Gardul va fi livrat cu o placuta cu stema companiei producatoare pe una dintre portile gardului achizitionat. Placuta va fi amplasata pe poarta pietonala sau, in absenta acesteia, pe o alta poarta sau modul al gardului conform deciziei companiei producatoare.`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // IV. ETAPELE EXECUTIEI
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "IV. ETAPELE EXECUTIEI" }]
            },
            {
              type: "orderedList",
              attrs: { start: 1 },
              content: [
                {
                  type: "listItem",
                  content: [{
                    type: "paragraph",
                    attrs: { textAlign: "left" },
                    content: [{ type: "text", text: "1. Efectuarea masuratorilor la fata locului." }]
                  }]
                },
                {
                  type: "listItem",
                  content: [{
                    type: "paragraph",
                    attrs: { textAlign: "left" },
                    content: [{ type: "text", text: "2. Confectionarea gardului in atelier: prelucrare, debitare, gaurire, vopsire." }]
                  }]
                },
                {
                  type: "listItem",
                  content: [{
                    type: "paragraph",
                    attrs: { textAlign: "left" },
                    content: [{ type: "text", text: "3. Montajul gardului la adresa indicata de beneficiar pana la punctul de functionalitate completa." }]
                  }]
                }
              ]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Masuratorile in baza carora se executa gardul precum si montajul acestuia cad in sarcina executantului. Executantul se obliga sa efectueze masuratorile la fata locului si este direct responsabil de acestea."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Dupa efectuarea masuratorilor finale de catre executant oferta de pret va fi refacuta cu noile dimensiuni (pretul pentru metru patrat va ramane acelasi ca in oferta initiala) si se va intocmi un act aditional la contract in care se va specifica noul pret."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // V. TERMEN DE EXECUTIE
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "V. TERMEN DE EXECUTIE" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Efectuarea masuratorilor la fata locului se va face in momentul in care partea de lucrare ce cade in sarcina beneficiarului, si anume, partea de zidarie, beton, stalpi de metal, etc., pe care se vor aplica reperele din aluminiu ce vor fi confectionate de executant, este complet finisata si nu se vor mai aduce modificari ulterioare."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "In caz contrar, costul pentru o a doua deplasare va cadea in sarcina beneficiarului. Pretul se va stabili de catre executant in functie de locatia unde va trebui sa fie facuta deplasarea, si va fi stabilit print-un act aditional la contract."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.p1TermenExecutie }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.p2TermenExecutie }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // VI. PRETUL SI MODALITATI DE PLATA
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "VI. PRETUL SI MODALITATI DE PLATA" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Valoarea intregii lucrari contractate este de ${this.sumaContract.toFixed(2)} euro.`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Plata se va face in doua etape, dupa cum urmeaza:`
              }]
            },
            {
              type: "bulletList",
              attrs: { textAlign: "left" },
              content: [
                ...this.plata1,
                {
                  type: "listItem",
                  content: [{
                    type: "paragraph",
                    attrs: { textAlign: "left" },
                    content: [{ type: "text", text: this.plata2 }]
                  }]
                }
              ]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Pentru neachitarea facturii la termenul de scadenta, beneficiarul datoreaza penalitati de intarziere de 10 euro / zi."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Pentru nerespectarea termenului de executie si montaj, executantul datoreaza penalitati de intarziere de 10 euro / zi."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Penalitatile de intarziere pentru neachitarea facturii la termen, se calculeaza din ziua imediat urmatoare expirarii termenului de scadenta al facturii si pana la data stingerii sumei datorate."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Penalitatile de intarziere pentru nerespectarea termenului de executie si montaj, se calculeaza din ziua imediat urmatoare expirarii termenului de executie si montaj si pana la data finalizarii complete a lucrarii."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "In cazul in care, la semnarea procesului verbal de predare-primire beneficiarul mentioneaza anumite neconformitati cu privire la produs sau la montajul acestuia, se poate retine pana la finalizarea completa a lucrarii 20% din valoarea facturii restante, dar nu mai mult de 500 euro."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Facturarea se face in lei la cursul de vanzare din ziua respectiva al bancii ${this.executantDetails.bank_name}.`
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Plata se face prin ordin de plata sau depunere numerar in contul specificat pe factura."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // VII. TERMEN DE REMEDIERE
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "VII. TERMEN DE REMEDIERE" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Termenul de remediere a unor defectiuni aparute din cauza executantului ulterior montajului, este de maximum 30 zile lucratoare de la data transmiterii solicitarii de catre beneficiar."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Daca se constata ca defectiunea s-a produs din cauza beneficiarului, cheltuielile vor intra in sarcina acestuia."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // VIII. GARANTIA
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "VIII. GARANTIA" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Se acorda garantie pentru vopsea pe o perioada de 5 ani pentru defecte de material sau aplicare, iar pentru accesorii si lucrare se acorda garantie pe o perioadă de 24 luni de la data receptionarii lucrarii."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // IX. AUTORIZAȚII
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "IX. AUTORIZAȚII" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Obținerea autorizației de construire, dacă este necesară conform legislației în vigoare, precum și a oricăror alte avize sau aprobări necesare pentru realizarea lucrării, cade exclusiv în sarcina BENEFICIARULUI."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "EXECUTANTUL și MONTATORUL nu pot fi considerați răspunzători pentru lipsa acestor autorizații sau pentru consecințele rezultate din neobținerea lor de către BENEFICIAR."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // X. PRELUCRAREA DATELOR PERSONALE
            {
              type: "heading",
              attrs: { textAlign: "left", level: 2 },
              content: [{ type: "text", text: "X. PRELUCRAREA DATELOR PERSONALE" }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "In temeiul prezentului contract si al Regulamentului UE nr. 679/2016, partile iau la cunostinta si accepta faptul ca una din ele ar putea colecta anumite date cu caracter personal ale reprezentantilor celeilalte (reprezentantul legal precum si alte persoane care furnizeaza asemenea date), cum ar fi: nume, adrese de e-mail si numere de telefon mobil."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Stocarea datelor personale obtinute in baza prezentului contract se va face in format electronic, cat si in format listat pe suport de hartie."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Durata de stocare a datelor este limitata la ceea ce este necesar pentru scopurile de procesare si vor fi pastrate pe perioada de valabilitate a contractului dupa care vor fi sterse."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Datele cu caracter personal colectate de catre parti nu vor fi transferate catre alte terte persoane, fizice sau juridice."
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: "Oricare din cele doua parti are dreptul de a solicita rectificarea, completarea, restrictionarea prelucrarii sau stergerea datelor cu caracter personal."
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // Contract conclusion
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                text: `Prezentul contract a fost incheiat astazi ${DateTime.fromISO(String(this.contract.data)).toFormat("dd.MM.yyyy")} in ${this.exemplareContract} exemplare, cate unul pentru fiecare parte.`
              }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },

            // Signatures
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "EXECUTANT"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.executantDetails.name }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "left" },
              content: [{ type: "text", text: this.executantDetails.legal_representative }]
            },
            { type: "paragraph", attrs: { textAlign: "left" } },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{
                type: "text",
                marks: [{ type: "bold" }],
                text: "BENEFICIAR"
              }]
            },
            {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.nume.toUpperCase() }]
            },
            this.contract.persoana ? {
              type: "paragraph",
              attrs: { textAlign: "right" },
              content: [{ type: "text", text: this.contract.persoana }]
            } : { type: "blockquote" },
            { type: "paragraph", attrs: { textAlign: "left" } },
            this.isMontajSeparat ? this.formatParagraph("MONTATOR", "left", "bold") : { type: "blockquote" }
          ]
        }
      },

      // PDF Generation
      async generatePdf(documentText) {
        const doc = await pdf_contract.createContractPDF(documentText.content, contract.country, CONTRACT_FORM_CODE)
        return doc
      }
    }
  }
}